import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppId, tryParseAppId } from '@rockwell-automation-inc/common-utils';
import { LoggerService } from '@servicesV2/logger.service';
import { DateTime } from 'luxon';
import { delayWhen, distinctUntilChanged, filter, map, Observable, timer } from 'rxjs';
import {
  AppState,
  FlagName,
  FlagValue,
  IEntitlementDeallocationFlagManager,
  IMaintenanceWindow,
  PreviewAppId,
} from '../lemans-app.model';
import { selectFlags, selectTileMaintenanceWindowFlags } from './feature-flags.selectors';

// REVISIT: only used in test directly - isn't there a better way to hide this class
export class MaintenanceWindow implements IMaintenanceWindow {
  public static readonly Inactive: IMaintenanceWindow = Object.freeze({
    from: DateTime.fromMillis(0),
    duration: 0,
    to: DateTime.fromMillis(0),
    isActive: false,
    customMessage: undefined,
    startsWithinDays: (_days: number): boolean => false,
  });
  from: DateTime;
  duration: number;
  customMessage?: string;
  public static create(json: string, logger?: LoggerService): IMaintenanceWindow {
    try {
      const obj = (json && JSON.parse(json)) || {};
      if (Object.keys(obj).length === 0) {
        return MaintenanceWindow.Inactive;
      }
      return new MaintenanceWindow(obj, logger);
    } catch (ex) {
      logger?.warn('Failed to parse MaintenanceWindow from flag value; Maintenance Window not activated', ex, json);
      return MaintenanceWindow.Inactive;
    }
  }
  private constructor(obj: any, logger?: LoggerService) {
    this.duration = Number.parseInt(obj.duration) || 0;
    this.from = DateTime.fromISO(obj.from);
    if (!this.from.isValid) {
      logger?.error('Failed to parse from date - ', this.from.invalidReason, this.from.invalidExplanation);
      this.from = DateTime.fromMillis(0);
    }
    this.customMessage = typeof obj.message === 'string' && obj.message.trim() ? obj.message.trim() : undefined;
  }

  get to(): DateTime {
    return this.from.plus({ minutes: this.duration });
  }
  get isActive(): boolean {
    const now = DateTime.now();
    return this.duration > 0 && now >= this.from && now <= this.from.plus({ minutes: this.duration });
  }
  startsWithinDays(days: number): boolean {
    const now = DateTime.now();
    return this.duration > 0 && now >= this.from.minus({ days: days }) && now <= this.from;
  }
}

/// Resolved per-service-kind deallocation configuration:
/// - `{ kind: 'disabled' }`: the service kind is not deallocation-enabled.
/// - `{ kind: 'none' }`: threshold was empty/blank (or omitted) — any allocation date is eligible.
/// - `{ kind: 'invalid' }`: threshold was present but malformed/unparseable — fails closed (always ineligible).
/// - `{ kind: 'valid', value }`: the parsed allocated-date threshold, compared as a complete instant.
type ServiceDeallocationConfiguration =
  { kind: 'disabled' } | { kind: 'none' } | { kind: 'invalid' } | { kind: 'valid'; value: DateTime };

/// Per-service-kind configuration map: a missing key means the service kind is not deallocation-enabled;
/// present keys carry their resolved deallocation configuration (see above).
type ServiceDeallocationConfigurations = Map<AppId, ServiceDeallocationConfiguration>;
type DeallocateEnabledServices = 'ALL' | 'NONE' | ServiceDeallocationConfigurations;

export class EntitlementDeallocationFlagManager implements IEntitlementDeallocationFlagManager {
  deallocateEnabledForAppIds: DeallocateEnabledServices = 'NONE';

  private constructor(
    flagvalue: string,
    private readonly logger?: LoggerService,
  ) {
    try {
      const trimmedFlagValue = flagvalue?.trim();

      if (trimmedFlagValue === 'NONE' || trimmedFlagValue === 'ALL') {
        this.deallocateEnabledForAppIds = trimmedFlagValue;
        return;
      }

      const parsedFlagValuesArr = (trimmedFlagValue && JSON.parse(trimmedFlagValue)) || [];
      const deallocationConfigurations: ServiceDeallocationConfigurations = new Map();
      parsedFlagValuesArr.forEach((flagValueEntry: unknown) => {
        if (!flagValueEntry || typeof flagValueEntry !== 'object' || Array.isArray(flagValueEntry)) {
          this.logger?.error('Invalid DeallocatableEntSks entry found in flag value; skipping', flagValueEntry);
          return;
        }
        const { serviceKind, enableDeallocationAfter } = flagValueEntry as {
          serviceKind?: string;
          enableDeallocationAfter?: string;
        };
        const parsedServiceKind = tryParseAppId(serviceKind ?? '');
        if (!parsedServiceKind) {
          this.logger?.error(`Invalid app ID "${serviceKind}" found in DeallocatableEntSks flag value; skipping`);
          return;
        }
        deallocationConfigurations.set(parsedServiceKind, this.parseThreshold(enableDeallocationAfter));
      });
      this.deallocateEnabledForAppIds = deallocationConfigurations.size > 0 ? deallocationConfigurations : 'NONE';
    } catch (ex) {
      this.logger?.error('Failed to parse DeallocatableEntSks from flag value; Defaulting to NONE', ex, flagvalue);
      this.deallocateEnabledForAppIds = 'NONE';
    }
  }

  private parseThreshold(enableDeallocationAfter: string | undefined): ServiceDeallocationConfiguration {
    if (!enableDeallocationAfter || (typeof enableDeallocationAfter === 'string' && !enableDeallocationAfter.trim())) {
      // Missing/blank threshold for a configured service kind is permissive: any allocation date is eligible.
      return { kind: 'none' };
    }
    if (typeof enableDeallocationAfter !== 'string') {
      return { kind: 'invalid' };
    }
    const trimmedThreshold = enableDeallocationAfter.trim();
    // Luxon interprets offset-less timestamps in the local zone and honors supplied offsets.
    const parsedDate = DateTime.fromISO(trimmedThreshold);
    if (!parsedDate.isValid) {
      this.logger?.warn(
        `Invalid enableDeallocationAfter "${enableDeallocationAfter}" in DeallocatableEntSks flag value; ` +
          'a valid ISO 8601 timestamp is required. Blocking deallocation for the configured service kind.',
      );
      return { kind: 'invalid' };
    }
    return { kind: 'valid', value: parsedDate };
  }

  private getServiceDeallocationConfiguration(serviceKind: AppId): ServiceDeallocationConfiguration {
    if (this.deallocateEnabledForAppIds === 'ALL') {
      return { kind: 'none' };
    }
    if (this.deallocateEnabledForAppIds === 'NONE') {
      return { kind: 'disabled' };
    }
    return this.deallocateEnabledForAppIds.get(serviceKind) ?? { kind: 'disabled' };
  }

  /// Evaluates Harness feature enablement and, where applicable, the allocated-date threshold for a
  /// service kind. `isCustomProvisioningPending` distinguishes the two call sites:
  /// - Omitted: Allocation ID mode — the threshold (if configured) is always evaluated against `allocationDate`.
  /// - `false`: ordinary Active entitlement — only service-kind enablement is checked; the threshold is not
  ///   applied.
  /// - `true`: the full-quantity action on an entitlement whose custom provisioning is incomplete — the
  ///   threshold (if configured) is evaluated against `allocationDate`.
  /// Entitlement status, Universal Credits, and tenant-permission checks are the caller's responsibility; this
  /// method must only be called with a resolved `AppId` service kind.
  canDeallocate(serviceKind: AppId, allocationDate: Date, isCustomProvisioningPending?: boolean): boolean {
    const deallocationConfiguration = this.getServiceDeallocationConfiguration(serviceKind);
    if (isCustomProvisioningPending === false) {
      return deallocationConfiguration.kind !== 'disabled';
    }
    switch (deallocationConfiguration.kind) {
      case 'disabled':
        return false;
      case 'none':
        // Threshold was empty/blank - any allocation date is eligible.
        return true;
      case 'invalid':
        // Threshold was present but malformed - fails closed.
        return false;
      case 'valid': {
        // The API returns timestamps as ISO strings at runtime, so normalise to a Date before using Luxon.
        const allocatedAt = DateTime.fromJSDate(new Date(allocationDate));
        if (!allocatedAt.isValid) {
          this.logger?.warn(
            `Missing or invalid allocation date "${allocationDate}" for service kind "${serviceKind}"; blocking deallocation.`,
          );
          return false;
        }
        return allocatedAt >= deallocationConfiguration.value;
      }
      default:
        this.logger?.warn(
          `Unexpected deallocation configuration for service kind "${serviceKind}"; blocking deallocation.`,
          deallocationConfiguration,
        );
        return false;
    }
  }

  public static create(json: string, logger?: LoggerService): IEntitlementDeallocationFlagManager {
    return new EntitlementDeallocationFlagManager(json, logger);
  }
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsFacade {
  private readonly store$ = inject<Store<AppState>>(Store);
  private readonly logger = inject(LoggerService).withContext('FeatureFlagsFacade');

  flags$ = this.store$.select(selectFlags);
  tilesUnderMaintenance$ = this.store$.select(selectTileMaintenanceWindowFlags);

  getFlagValue$(flag: FlagName): Observable<FlagValue> {
    return this.flags$.pipe(
      map((flags) => (flags ? flags[flag] : undefined)),
      distinctUntilChanged(),
    );
  }

  getTileMaintenanceStatus$(appId: AppId | PreviewAppId): Observable<boolean> {
    const flagName = `tile_${appId}_maintenance`;
    return this.tilesUnderMaintenance$.pipe(
      map((tileUnderMaintenance) => (tileUnderMaintenance ? (tileUnderMaintenance[flagName] as boolean) : false)),
      distinctUntilChanged(),
    );
  }
  maintenanceWindow$: Observable<IMaintenanceWindow> = this.getFlagValue$('maintenance_window').pipe(
    map((json: string) => MaintenanceWindow.create(json, this.logger)),
    distinctUntilChanged(),
  );

  /// sets up a delayed observable that triggers at the end time if the mw was active.
  maintenanceWindowCompleted$: Observable<any> = this.maintenanceWindow$.pipe(
    filter((mw) => mw.isActive),
    delayWhen((mw: IMaintenanceWindow, _idx) => timer(mw.to.toJSDate())),
  );

  allowDeallocationFlag$ = this.getFlagValue$('deallocatable_ent_sks').pipe(
    map((sksFlags: string) => EntitlementDeallocationFlagManager.create(sksFlags, this.logger)),
    distinctUntilChanged(),
  );
}
